import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

import { useElasticQuery } from "@/api/mutations";
import LoadingSmall from "@/components/LoadingSmall";
import Table from "@/components/Table";
import useGetColours from "@/helpers/getColours";
import {
  buildFilters,
  twoDp,
} from "@/helpers/helpers";
import useAppStore from "@/store/appStateZustand";

import Tabs from "./_tabs";
import { indexReducedPattern } from "./constants";
import IncomeReveal from "./Income/_incomeReveal";
import UsageReveal from "./Income/_usageReveal";

function IncomeCellEarnings({ row: { original } }) {
  const currencySymbol = useAppStore(s => s.tenantCurrencySymbol);
  return (
    <span>
      {`${currencySymbol}${twoDp(original?.WriterValues?.value)}`}
    </span>
  );
}

function IncomeExpanderHeader() {
  return null;
}

function IncomeExpanderCell({ row }) {
  return (
    <span>
      {row.isExpanded ? <FaChevronUp /> : <FaChevronDown />}
    </span>
  );
}

function UsageCellEarnings({ row: { original } }) {
  const currencySymbol = useAppStore(s => s.tenantCurrencySymbol);
  return (
    <span>
      {`${currencySymbol}${twoDp(original?.WriterValues?.value)}`}
    </span>
  );
}

function UsageExpanderHeader() {
  return null;
}

function UsageExpanderCell({ row }) {
  return (
    <span>
      {row.isExpanded ? <FaChevronUp /> : <FaChevronDown />}
    </span>
  );
}

function IncomeSection({ matchAccounts, dateType }) {
  const { t } = useTranslation("other");
  const [pending, setPending] = useState(true);
  const [result, setResult] = useState(null);

  const { highlightColour } = useGetColours();

  const range = useAppStore(s => s.range);

  const flags = useFlags();
  const query = {
    index: indexReducedPattern,
    body: {
      query: {
        bool: {
          filter: [
            {
              range: { [dateType]: range[dateType] },
            },
            ...buildFilters(flags, matchAccounts),
          ],
        },
      },
      aggs: {
        IncomeTypes: {
          terms: {
            field: "incomeType.keyword",
            size: 500,
            order: {
              WriterValues: "desc",
            },
            missing: "N/A",
          },
          aggs: {
            WriterValues: {
              sum: {
                field: "ownerWriterPayment",
              },
            },
          },
        },
        UsageTypes: {
          terms: {
            field: "usage.keyword",
            size: 500,
            order: {
              WriterValues: "desc",
            },
            missing: "N/A",
          },
          aggs: {
            WriterValues: {
              sum: {
                field: "ownerWriterPayment",
              },
            },
          },
        },
      },
    },
    size: 0,
  };

  const elasticQueryMutation = useElasticQuery();
  useEffect(() => {
    setPending(true);

    elasticQueryMutation.mutate(query, {
      onSuccess: (data) => {
        // console.log('income data', data);
        setPending(false);
        setResult(data);
      },
      onError: (error) => {
        console.log(error);
        setPending(false);
        setResult(null);
      },
    });
  }, [range, dateType]);

  const columns = React.useMemo(
    () => [
      {
        Header: t("other:income-type-tab"),
        accessor: row => `${row?.key}`,
        width: "25%",
      },
      {
        Header: t("other:earnings2"),
        accessor: r => r.WriterValues.value,
        Cell: IncomeCellEarnings,
        width: "20%",
      },
      {
        // Make an expander cell
        Header: IncomeExpanderHeader,
        id: "expander", // It needs an ID
        width: "5%",

        Cell: IncomeExpanderCell,
        // We can override the cell renderer with a SubCell to be used with an expanded row
        SubCell: () => null, // No expander on an expanded row
      },

    ],
    [],
  );

  const columnsUsage = React.useMemo(
    () => [
      {
        Header: t("other:usage-type-tab"),
        accessor: row => `${row?.key}`,
        width: "25%",
      },
      {
        Header: t("other:earnings2"),
        accessor: r => r.WriterValues.value,
        Cell: UsageCellEarnings,
        width: "20%",
      },
      {
        // Make an expander cell
        Header: UsageExpanderHeader,
        id: "expander", // It needs an ID
        width: "5%",

        Cell: UsageExpanderCell,
        // We can override the cell renderer with a SubCell to be used with an expanded row
        SubCell: () => null, // No expander on an expanded row
      },

    ],
    [],
  );

  if (!pending && result) {
    return (
      <>
        <Tabs
          tabs={[
            t("other:income-tab-header"),
            t("other:usage-tab-header"),
          ]}
          objects={[

            <div key="income-table" className="pt-4">
              <Table
                data={result?.body?.aggregations?.IncomeTypes?.buckets || []}
                columns={columns}
                highlightColour={highlightColour}
                revealComponent={(
                  <IncomeReveal matchAccounts={matchAccounts} dateType={dateType} />
                )}
                searchSuffix={t("common:writers")}
                disableSearch
                initialSort={[{
                  id: "Earnings",
                  desc: true,
                }]}
              />

            </div>,
            <div key="usage-table" className="pt-4">
              <Table
                data={result?.body?.aggregations?.UsageTypes?.buckets || []}
                columns={columnsUsage}
                highlightColour={highlightColour}
                revealComponent={(
                  <UsageReveal matchAccounts={matchAccounts} dateType={dateType} />
                )}
                searchSuffix={t("common:writers")}
                disableSearch
                initialSort={[{
                  id: "Earnings",
                  desc: true,
                }]}
              />

            </div>,
          ]}
        />
      </>

    );
  }
  return (
    <div className="p-4">
      <LoadingSmall />

    </div>
  );
}

export default IncomeSection;
